import React from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  get,
  UseControllerProps,
  UseFormStateReturn,
} from 'react-hook-form';

import { useTranslator } from '@/hooks/useTranslator';

type OriginalRenderProp<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ({
  field,
  fieldState,
  formState,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<TFieldValues>;
}) => React.ReactElement;

export type ControllerWithErrorParam<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName> & {
    error: boolean;
    label: string;
    helperText: string | undefined;
  };
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<TFieldValues>;
};

type ControllerWithErrorProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
  label: string;
  render: ({
    field,
    fieldState,
    formState,
  }: ControllerWithErrorParam<TFieldValues, TName>) => React.ReactElement;
};

export const ControllerWithError = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  render,
  label,
  ...props
}: ControllerWithErrorProps<TFieldValues, TName, TTransformedValues>): React.ReactElement => {
  const tr = useTranslator();

  return (
    <Controller
      {...props}
      render={({
        field,
        fieldState,
        formState,
      }: Parameters<OriginalRenderProp<TFieldValues, TName>>[0]): React.ReactElement => {
        const error = get(formState.errors, props.name);
        const errorMessage = error?.message ? tr(error.message?.toString(), label) : undefined;

        return render({
          field: {
            ...field,
            label,
            error: !!error,
            helperText: errorMessage,
          },
          fieldState,
          formState,
        });
      }}
    />
  );
};
